// SkipJobsOverviewPage — /reports/skip-management/jobs.
//
// Job volume, revenue and status mix over a date range. KPI cards + two bar
// charts (by status, by type) + a sortable per-status table with CSV export.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { DateRangeFilter, lastThirtyDays } from './components/DateRangeFilter';
import { KpiGrid, type KpiTile } from './components/KpiGrid';
import { BarChart } from './components/BarChart';
import { SortableTable, type SortableColumn } from './components/SortableTable';
import { gbp, formatNumber, pct } from './components/format';
import { useJobsOverview, type DateRange } from './useSkipReports';
import type { ReportCell } from '../useReports';
import styles from './skipReports.module.css';

const TABLE_COLUMNS: SortableColumn[] = [
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'jobs', label: 'Jobs', type: 'number' },
  { key: 'revenue', label: 'Revenue', type: 'currency' },
];

export function SkipJobsOverviewPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<DateRange>(lastThirtyDays());
  const [submitted, setSubmitted] = useState<DateRange>(form);
  const enabled = !!submitted.date_from && !!submitted.date_to;

  const query = useJobsOverview(submitted, enabled);
  const data = query.data ?? null;

  const kpis = useMemo<KpiTile[]>(() => {
    if (!data) return [];
    return [
      { label: 'Total jobs', value: formatNumber(data.kpis.total_jobs), tone: 'blue' },
      { label: 'Total revenue', value: gbp(data.kpis.total_revenue), tone: 'purple' },
      { label: 'Completed', value: formatNumber(data.kpis.completed_jobs), tone: 'green' },
      { label: 'Cancellation rate', value: pct(data.kpis.cancellation_rate), tone: 'red' },
    ];
  }, [data]);

  const tableRows = useMemo<Record<string, ReportCell>[]>(
    () =>
      (data?.by_status ?? []).map((r) => ({
        status: r.status,
        jobs: r.jobs,
        revenue: r.revenue,
      })),
    [data],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title="Jobs Overview"
        subtitle="Job volume, revenue and status mix over a date range"
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/reports/skip-management')}>
            Back
          </Button>
        }
      />

      <DateRangeFilter
        form={form}
        onChange={setForm}
        onSubmit={() => setSubmitted({ ...form })}
        loading={query.isFetching}
      />

      {query.isFetching && !data ? (
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      ) : null}

      {data ? <KpiGrid tiles={kpis} /> : null}

      {data ? (
        <Card title="Jobs by status">
          <BarChart
            data={data.by_status.map((r) => ({ label: r.status, value: r.jobs }))}
            colour="var(--color-secondary, #2563eb)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Jobs by type">
          <BarChart
            data={data.by_type.map((r) => ({ label: r.type_name, value: r.jobs }))}
            colour="var(--color-status-confirmed_text, #1e40af)"
          />
        </Card>
      ) : null}

      {data ? (
        <Card title="Status breakdown" flush>
          <SortableTable columns={TABLE_COLUMNS} rows={tableRows} exportName="skip-jobs-overview" />
        </Card>
      ) : null}

      {!query.isFetching && !data ? (
        <Card>
          <p className={styles.empty}>
            <BarChart3 size={20} aria-hidden /> Pick a date range and refresh to see the report.
          </p>
        </Card>
      ) : null}
    </div>
  );
}
